import React, { useState } from "react";
import "./MassReassignMenu.css";

const MassReassignMenu = ({ leads = [], users = [], selectedLeadIds = [], onReassign, onClose }) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [primaryUsers, setPrimaryUsers] = useState([]);
  const [secondaryUsers, setSecondaryUsers] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState("legacy"); // "legacy" or "multi"

  const selectedLeads = leads.filter(
    (lead) => selectedLeadIds.includes(lead.id.toString())
  );
  const totalSelected = selectedLeads.length;

  const selectedLeadsBreakdown = selectedLeads.reduce((acc, lead) => {
    acc[lead.status] = (acc[lead.status] || 0) + 1;
    return acc;
  }, {});


  const currentUserBreakdown = selectedUser
    ? leads
        .filter((lead) => lead.assigned_to === parseInt(selectedUser, 10))
        .reduce((acc, lead) => {
          acc[lead.status] = (acc[lead.status] || 0) + 1;
          return acc;
        }, {})
    : {};

  const toReassignBreakdown = selectedUser
    ? selectedLeads
        .filter((lead) => lead.assigned_to !== parseInt(selectedUser, 10))
        .reduce((acc, lead) => {
          acc[lead.status] = (acc[lead.status] || 0) + 1;
          return acc;
        }, {})
    : { ...selectedLeadsBreakdown };

  const statuses = Array.from(
    new Set([
      ...Object.keys(selectedLeadsBreakdown),
      ...Object.keys(toReassignBreakdown),
      ...Object.keys(currentUserBreakdown),
    ])
  );

  const totalSelectedBreakdown = statuses.reduce(
    (sum, status) => sum + (selectedLeadsBreakdown[status] || 0),
    0
  );

  const currentTotal = statuses.reduce(
    (sum, status) => sum + (currentUserBreakdown[status] || 0),
    0
  );
  const toAddTotal = statuses.reduce(
    (sum, status) => sum + (toReassignBreakdown[status] || 0),
    0
  );
  const newTotal = currentTotal + toAddTotal;

  const handleReassign = () => {
    if (assignmentMode === "legacy") {
      if (selectedUser) {
        onReassign(selectedUser);
        onClose();
      }
    } else {
      // Multi-user assignment mode
      if (primaryUsers.length > 0 || secondaryUsers.length > 0) {
        onReassign({
          mode: "multi",
          primary: primaryUsers,
          secondary: secondaryUsers,
          selectedIds: selectedLeadIds
        });
        onClose();
      }
    }
  };

  return (
    <div className="mass-reassign-menu">
      <h3>Mass Reassign ({totalSelected} selected)</h3>
      
      {/* Assignment Mode Selector */}
      <div className="assignment-mode-selector">
        <label>
          <input
            type="radio"
            value="legacy"
            checked={assignmentMode === "legacy"}
            onChange={() => setAssignmentMode("legacy")}
          />
          Legacy (Single User)
        </label>
        <label>
          <input
            type="radio"
            value="multi"
            checked={assignmentMode === "multi"}
            onChange={() => setAssignmentMode("multi")}
          />
          Multiple Users (Primary & Secondary)
        </label>
      </div>

      <div className="summary">
        <h4>Selected Leads Breakdown</h4>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {statuses.map((status) => (
              <tr key={status}>
                <td>{status}</td>
                <td>{selectedLeadsBreakdown[status] || 0}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td>Total</td>
              <td>{totalSelectedBreakdown}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Conditional Rendering based on Assignment Mode */}
      {assignmentMode === "legacy" ? (
        <>
          <div className="reassign-control">
            <label htmlFor="reassignUser">Reassign to: </label>
            <select
              id="reassignUser"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="">Select a user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>
          {selectedUser && (
            <div className="user-breakdown">
          <h4>Breakdown for Selected User</h4>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Current</th>
                <th>+ (Reassign)</th>
                <th>New Total</th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((status) => {
                const current = currentUserBreakdown[status] || 0;
                const toAdd = toReassignBreakdown[status] || 0;
                return (
                  <tr key={status}>
                    <td>{status}</td>
                    <td>{current}</td>
                    <td>{toAdd > 0 ? `+${toAdd}` : toAdd}</td>
                    <td>{current + toAdd}</td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td>Total</td>
                <td>{currentTotal}</td>
                <td>{toAddTotal > 0 ? `+${toAddTotal}` : toAddTotal}</td>
                <td>{newTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>
          )}
        </>
      ) : (
        <div className="multi-assignment-controls">
          <div className="assignment-group">
            <label>Primary Users:</label>
            <div className="selected-users-chips">
              {primaryUsers.map((userId) => {
                const user = users.find(u => u.id === parseInt(userId));
                if (!user) return null;
                return (
                  <div
                    key={userId}
                    className="user-chip"
                    style={{ backgroundColor: user.chip_color || "#e0e0e0" }}
                  >
                    <span>{user.first_name} {user.last_name}</span>
                    <button
                      type="button"
                      onClick={() => setPrimaryUsers(primaryUsers.filter(id => id !== userId))}
                      className="remove-chip-btn"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value && !primaryUsers.includes(e.target.value)) {
                  setPrimaryUsers([...primaryUsers, e.target.value]);
                }
              }}
            >
              <option value="">-- Add Primary User --</option>
              {users.filter(u => !primaryUsers.includes(u.id.toString())).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className="assignment-group">
            <label>Secondary Users:</label>
            <div className="selected-users-chips">
              {secondaryUsers.map((userId) => {
                const user = users.find(u => u.id === parseInt(userId));
                if (!user) return null;
                return (
                  <div
                    key={userId}
                    className="user-chip"
                    style={{ backgroundColor: user.chip_color || "#e0e0e0" }}
                  >
                    <span>{user.first_name} {user.last_name}</span>
                    <button
                      type="button"
                      onClick={() => setSecondaryUsers(secondaryUsers.filter(id => id !== userId))}
                      className="remove-chip-btn"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value && !secondaryUsers.includes(e.target.value)) {
                  setSecondaryUsers([...secondaryUsers, e.target.value]);
                }
              }}
            >
              <option value="">-- Add Secondary User --</option>
              {users.filter(u => !secondaryUsers.includes(u.id.toString())).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="actions">
        <button 
          onClick={handleReassign} 
          disabled={
            assignmentMode === "legacy" 
              ? !selectedUser 
              : (primaryUsers.length === 0 && secondaryUsers.length === 0)
          }
        >
          Reassign
        </button>
        <button onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
};

export default MassReassignMenu;
